<template>
  <div class="sales-reports-table lov-card">
    <h6 class="lov-card-title mb-3">{{ detailTitle }}</h6>
    <div class="table-responsive lov-table-wrap">
      <b-table
        :items="tableItems"
        :fields="tableFields"
        small
        hover
        show-empty
        :empty-text="emptyText"
        :busy="loading"
        :tbody-tr-class="rowClass"
        class="lov-table mb-0"
      >
        <template #table-busy>
          <div class="text-center text-muted py-4">Đang tải dữ liệu...</div>
        </template>

        <template #cell(stt)="slotData">
          <span v-if="!slotData.item._isTotal">{{ rowIndex(slotData.index) }}</span>
        </template>

        <template #cell(tenHang)="slotData">
          <div
            v-if="isProductCell(slotData.item)"
            class="product-cell"
          >
            <div class="product-thumb">
              <img
                v-if="slotData.item.imageUrl"
                :src="slotData.item.imageUrl"
                alt=""
              />
              <i
                v-else
                class="far fa-image text-muted"
              />
            </div>
            <div class="product-meta">
              <div
                v-if="productCode(slotData.item)"
                class="product-code"
              >
                {{ productCode(slotData.item) }}
              </div>
              <div class="product-name">
                {{ slotData.item.tenHang || slotData.item.productName || '—' }}
              </div>
            </div>
          </div>
          <span v-else>{{ slotData.item.tenHang || '—' }}</span>
        </template>

        <template #cell(tenCuaHang)="slotData">
          <div
            v-if="columnType('tenCuaHang') === 'store'"
            class="store-cell"
          >
            <div class="store-name">{{ slotData.item.tenCuaHang || '—' }}</div>
            <div
              v-if="slotData.item.maCuaHang && !slotData.item._isTotal"
              class="store-code"
            >
              {{ slotData.item.maCuaHang }}
            </div>
          </div>
          <span v-else>{{ slotData.item.tenCuaHang || '—' }}</span>
        </template>

        <template #cell(tenKhach)="slotData">
          <div
            v-if="columnType('tenKhach') === 'customer'"
            class="customer-cell"
          >
            <div class="customer-name">{{ slotData.item.tenKhach || '—' }}</div>
            <div
              v-if="slotData.item.maKhach"
              class="customer-code"
            >
              {{ slotData.item.maKhach }}
            </div>
          </div>
          <span v-else>{{ slotData.item.tenKhach || '—' }}</span>
        </template>

        <template #cell(loaiHoaDon)="slotData">
          <div
            class="bill-type-cell"
            :class="{ 'bill-type-cell--return': isReturnRow(slotData.item) }"
          >
            <span
              class="bill-type-dot"
              :style="{ background: billTypeColor(slotData.item) }"
            />
            <span>{{ slotData.item.loaiHoaDon || '—' }}</span>
            <b-badge
              v-if="isReturnRow(slotData.item)"
              variant="danger"
              class="ml-2"
            >
              Trả hàng
            </b-badge>
          </div>
        </template>

        <template #cell(tiTrong)="slotData">
          <div
            v-if="!slotData.item._isTotal"
            class="share-cell"
          >
            <span class="share-pct">{{ formatSharePct(slotData.item) }}</span>
            <div class="share-bar">
              <div
                class="share-bar-fill"
                :style="{ width: shareWidth(slotData.item) }"
              />
            </div>
          </div>
        </template>

        <template #cell(margin)="slotData">
          <b-badge
            v-if="slotData.item.margin !== null && slotData.item.margin !== undefined"
            :variant="marginBadgeVariant(slotData.item.margin)"
            class="margin-pill"
          >
            {{ formatMargin(slotData.item.margin) }}
          </b-badge>
          <span v-else>—</span>
        </template>

        <template #cell(ngayMuaBan)="slotData">
          <div
            v-if="!slotData.item._isTotal"
            class="daterange-cell"
          >
            <span class="daterange-from">{{ formatDate(slotData.item.ngayMua) }}</span>
            <span class="daterange-to">→ {{ formatDate(slotData.item.ngayBan) }}</span>
          </div>
        </template>

        <template #cell(tuoiTon)="slotData">
          <b-badge
            v-if="!slotData.item._isTotal && slotData.item.tuoiTon != null"
            variant="light"
            class="age-pill"
          >
            {{ slotData.item.tuoiTon }}d
          </b-badge>
          <span v-else>—</span>
        </template>

        <template #cell(nccMaPhu)="slotData">
          <div
            v-if="!slotData.item._isTotal"
            class="multiline-cell"
          >
            <div class="multiline-primary">{{ slotData.item.nhaCungCap || '—' }}</div>
            <div
              v-if="slotData.item.maPhuNcc"
              class="multiline-muted"
            >
              #{{ slotData.item.maPhuNcc }}
            </div>
          </div>
        </template>

        <template #cell(spcDmBrand)="slotData">
          <div
            v-if="!slotData.item._isTotal"
            class="multiline-cell"
          >
            <div class="multiline-primary">
              {{ slotData.item.tenSpCha || slotData.item.spc || '—' }}
            </div>
            <div
              v-if="spcSubLabel(slotData.item)"
              class="multiline-muted"
            >
              {{ spcSubLabel(slotData.item) }}
            </div>
          </div>
        </template>

        <template #cell(dealsheets)="slotData">
          <div
            v-if="!slotData.item._isTotal"
            class="ds-stack-cell"
          >
            <div>{{ formatDsValue(slotData.item.dealsheet1) }}</div>
            <div>{{ formatDsValue(slotData.item.dealsheet2) }}</div>
            <div>{{ formatDsValue(slotData.item.dealsheet3) }}</div>
          </div>
        </template>

        <template
          v-for="field in dynamicValueFields"
          #[`cell(${field.key})`]="slotData"
        >
          <span
            :key="field.key"
            :class="valueClass(field, slotData.item)"
          >
            <a
              v-if="field.type === 'link' && getCellValue(slotData.item, field)"
              href="javascript:void(0)"
              class="cell-link"
              @click.prevent="$emit('cell-link', { field, row: slotData.item })"
            >
              {{ formatValue(getCellValue(slotData.item, field), field, slotData.item) }}
            </a>
            <template v-else>
              {{ formatValue(getCellValue(slotData.item, field), field, slotData.item) }}
            </template>
          </span>
        </template>
      </b-table>
    </div>

    <SalesReportsPagination
      v-if="showPagination"
      :current-page="currentPage"
      :page-size="perPage"
      :total-items="totalItems"
      :total-pages="totalPages"
      @page-change="$emit('page-change', $event)"
    />
  </div>
</template>

<script>
import { get } from 'lodash';
import moment from 'moment';

import { convertPrice } from '@/utils/common';
import { BILL_TYPE_META } from '../constants';
import SalesReportsPagination from './SalesReportsPagination.vue';

export default {
  name: 'SalesReportsTable',
  components: { SalesReportsPagination },
  props: {
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    showPagination: { type: Boolean, default: false },
    totalItems: { type: Number, default: 0 },
    totalPages: { type: Number, default: 0 },
    currentPage: { type: Number, default: 1 },
    perPage: { type: Number, default: 50 },
    totalRow: { type: Object, default: null },
    emptyText: { type: String, default: 'Không có dữ liệu' },
    tabKey: { type: String, default: '' },
    tableTitle: { type: String, default: '' },
  },
  computed: {
    detailTitle() {
      const count = this.showPagination ? this.totalItems : this.rows.length;
      if (this.tableTitle) {
        return `${this.tableTitle} — ${count}`;
      }
      return `Chi tiết — ${count} dòng`;
    },
    tableFields() {
      return this.columns.map((column) => ({
        key: column.key,
        label: column.label,
        thClass: column.thClass || '',
        tdClass: column.tdClass || '',
      }));
    },
    dynamicValueFields() {
      const skipKeys = new Set([
        'tenHang',
        'loaiHoaDon',
        'tiTrong',
        'margin',
        'stt',
        'ngayMuaBan',
        'tuoiTon',
        'nccMaPhu',
        'spcDmBrand',
        'dealsheets',
      ]);
      const skipTypes = [
        'product',
        'margin-badge',
        'bill-type',
        'customer',
        'share-bar',
        'index',
        'age',
        'date-range-stacked',
        'ncc-multi',
        'spc-multi',
        'ds-stack',
      ];
      return this.columns.filter((column) => {
        if (skipKeys.has(column.key)) {
          return false;
        }
        if (column.key === 'tenKhach' && column.type === 'customer') {
          return false;
        }
        if (column.key === 'tenCuaHang' && column.type === 'store') {
          return false;
        }
        return !skipTypes.includes(column.type);
      });
    },
    tableItems() {
      if (!this.totalRow) {
        return this.rows;
      }
      return [...this.rows, { ...this.totalRow, _isTotal: true }];
    },
  },
  methods: {
    columnType(key) {
      return this.columns.find((c) => c.key === key)?.type;
    },
    rowIndex(index) {
      if (this.showPagination) {
        return (this.currentPage - 1) * this.perPage + index + 1;
      }
      return index + 1;
    },
    rowClass(item) {
      if (!item) {
        return '';
      }
      if (item._isTotal) {
        return 'row-total';
      }
      if (item.billType === 3) {
        return 'row-return';
      }
      return '';
    },
    isProductCell(row) {
      return (
        ['by-product', 'by-imei', 'by-customer-product', 'purchase-by-imei'].includes(
          this.tabKey,
        ) ||
        row.productId ||
        row.imageUrl
      );
    },
    productCode(row) {
      return row.maHang || row.productCode || row.barCode || '';
    },
    getCellValue(row, field) {
      if (field.key === 'imeiCode') {
        return row.imei || row.imeiCode;
      }
      return get(row, field.key);
    },
    valueClass(field, row) {
      if (row._isTotal) {
        return 'font-weight-bold';
      }
      if (field.type === 'currency-bold') {
        return 'cell-money cell-money--bold';
      }
      if (field.type === 'currency-muted') {
        return 'cell-money cell-money--muted';
      }
      if (field.type === 'currency-danger' || (row.billType === 3 && field.key === 'doanhThu')) {
        return 'cell-money cell-money--danger';
      }
      if (field.type === 'currency-success') {
        return 'cell-money cell-money--success';
      }
      if (field.type === 'number') {
        return 'cell-number';
      }
      return '';
    },
    formatValue(value, field, row) {
      if (row?._isTotal && (field.key === 'loaiHoaDon' || field.key === 'cayDanhMuc')) {
        return row.cayDanhMuc || row.loaiHoaDon || 'TỔNG';
      }
      if (row?._isTotal && ['maCuaHang', 'tenCuaHang', 'stt'].includes(field.key)) {
        return '';
      }
      if (value === null || value === undefined || value === '') {
        return row?._isTotal ? '' : '—';
      }
      if (
        ['currency', 'currency-bold', 'currency-muted', 'currency-danger', 'currency-success'].includes(
          field.type,
        )
      ) {
        const num = Number(value);
        // HĐ trả hàng (billType 3): doanh thu API trả dương nhưng phải hiển thị âm.
        if (row.billType === 3 && field.key === 'doanhThu') {
          return `-${convertPrice(Math.abs(num))}`;
        }
        // Giữ nguyên dấu (giá trị âm khi trả hàng ở báo cáo IMEI).
        return num < 0 ? `-${convertPrice(Math.abs(num))}` : convertPrice(num);
      }
      if (field.type === 'date-db') {
        return this.formatDate(value);
      }
      return value;
    },
    formatDate(value) {
      if (!value) {
        return '—';
      }
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
    },
    formatDsValue(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }
      const num = Number(value);
      return num < 0 ? `-${convertPrice(Math.abs(num))}` : convertPrice(num);
    },
    spcSubLabel(row) {
      return [row.danhMucCap1, row.thuongHieu].filter(Boolean).join(' · ');
    },
    formatMargin(value) {
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    formatSharePct(row) {
      const max = Math.max(...this.rows.map((r) => Number(r.tongGiaTriTonKho || 0)), 1);
      const pct = (Number(row.tongGiaTriTonKho || 0) / max) * 100;
      return `${pct.toFixed(1)}%`;
    },
    shareWidth(row) {
      const max = Math.max(...this.rows.map((r) => Number(r.tongGiaTriTonKho || 0)), 1);
      const pct = (Number(row.tongGiaTriTonKho || 0) / max) * 100;
      return `${Math.min(pct, 100)}%`;
    },
    marginBadgeVariant(value) {
      const percent = Number(value) * 100;
      if (percent >= 40) {
        return 'success';
      }
      if (percent >= 15) {
        return 'warning';
      }
      return 'secondary';
    },
    billTypeColor(row) {
      return BILL_TYPE_META[row.billType]?.color || '#9ca3af';
    },
    isReturnRow(row) {
      return row.billType === 3;
    },
  },
};
</script>

<style scoped>
.lov-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
}
.lov-card-title {
  font-weight: 600;
  color: #111827;
  margin: 0;
}
.lov-table-wrap {
  border-radius: 8px;
  /* Bảng nhiều cột (vd "Bán theo IMEI" 19 cột) tự cuộn ngang trong khung,
     không clip cột (overflow:hidden cũ giấu mất cột) và không kéo giãn layout. */
  overflow-x: auto;
  overflow-y: hidden;
  max-width: 100%;
}
.lov-table ::v-deep thead th {
  background: #f3f4f6 !important;
  color: #374151;
  font-weight: 600;
  font-size: 12px;
  border-bottom: 1px solid #e5e7eb !important;
  white-space: nowrap;
}
.lov-table ::v-deep tbody td {
  vertical-align: middle;
  font-size: 13px;
  border-top: 1px solid #f3f4f6 !important;
}
.lov-table ::v-deep tr.row-total td {
  background: #f9fafb;
  font-weight: 700;
  border-top: 2px solid #e5e7eb !important;
}
.lov-table ::v-deep tr.row-return td {
  color: #991b1b;
}
.product-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 200px;
}
.product-thumb {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}
.product-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.product-code,
.cell-link {
  font-size: 12px;
  color: #3b82f6;
  font-weight: 500;
}
.product-name,
.customer-name {
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}
.customer-code,
.store-code {
  font-size: 11px;
  color: #9ca3af;
}
.store-name {
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}
.bill-type-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bill-type-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.share-cell {
  min-width: 120px;
}
.share-pct {
  font-size: 12px;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
}
.share-bar {
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}
.share-bar-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 3px;
}
.cell-money {
  font-variant-numeric: tabular-nums;
}
.cell-money--bold {
  font-weight: 700;
  color: #111827;
}
.cell-money--muted {
  color: #6b7280;
}
.cell-money--danger {
  color: #dc2626;
}
.cell-money--success {
  color: #16a34a;
  font-weight: 600;
}
.cell-number {
  font-variant-numeric: tabular-nums;
}
.margin-pill {
  font-size: 12px;
  font-weight: 600;
  padding: 0.35em 0.65em;
}
.daterange-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  white-space: nowrap;
}
.daterange-from {
  color: #6b7280;
  font-size: 12px;
}
.daterange-to {
  font-weight: 600;
  color: #111827;
  font-size: 12px;
}
.age-pill {
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
}
.multiline-cell {
  line-height: 1.3;
  min-width: 150px;
}
.multiline-primary {
  color: #111827;
  font-size: 12px;
}
.multiline-muted {
  color: #9ca3af;
  font-size: 11px;
}
.ds-stack-cell {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
